import { BackgroundType, ClientSkill, Content, CustomImage, Font, ProductGraphicPreferenceStatus } from '../models';

export class SmartSequenceModel {
  slug: string; // productSlug (as it's the property name send by API, it can not be renamed)
  name: string;
  params: SmartSequenceParams;
  content: SmartSequenceContent;
  publishedDate?: string;
  updatedDate?: string;

  constructor(smartSequenceModel?: SmartSequenceModel) {
    this.slug = smartSequenceModel?.slug;
    this.name = smartSequenceModel?.name;
    this.params = new SmartSequenceParams(smartSequenceModel?.params);
    this.publishedDate = smartSequenceModel?.publishedDate;
    this.updatedDate = smartSequenceModel?.updatedDate;
    this.content = new SmartSequenceContent(smartSequenceModel?.content);
  }
}

export class SmartSequenceContent {
  sequences: SmartSequenceSequence[];
  intro?: {
    title: string;
    description: string;
    content?: Content;
  };
  outro?: {
    title: string;
    description: string;
    content?: Content;
  };

  constructor(content?: SmartSequenceContent) {
    this.sequences = (content?.sequences || []).map(s => new SmartSequenceSequence(s));
    this.intro = content?.intro
      ? {
          title: content?.intro?.title,
          description: content?.intro?.description,
          content: new Content(content?.intro?.content),
        }
      : undefined;
    this.outro = content?.outro
      ? {
          title: content?.outro?.title,
          description: content?.outro?.description,
          content: new Content(content?.outro?.content),
        }
      : undefined;
  }
}

export class SmartSequenceSequence {
  uuid: string;
  title: string;
  description?: string;
  questions: SmartSequenceQuestion[];
  questionsAnswered: SmartSequenceQuestion[];

  constructor(sequenceSequence?: SmartSequenceSequence) {
    this.uuid = sequenceSequence?.uuid;
    this.title = sequenceSequence?.title;
    this.description = sequenceSequence?.description;
    this.questions = (sequenceSequence?.questions || []).map(q => new SmartSequenceQuestion(q));
    this.questionsAnswered = (sequenceSequence?.questionsAnswered || []).map(q => new SmartSequenceQuestion(q));
  }
}

export class SmartSequenceQuestion {
  uuid: string;
  text: string;
  content: Content;
  skill?: ClientSkill;
  weight?: number;
  answers: SmartSequenceAnswer[];
  selectedAnswerUuid?: number;

  constructor(question?: SmartSequenceQuestion) {
    this.uuid = question?.uuid;
    this.text = question?.text;
    this.content = new Content(question?.content);
    this.skill = question.skill;
    this.weight = question?.weight;
    this.answers = (question?.answers || []).map(a => new SmartSequenceAnswer(a));
    this.selectedAnswerUuid = question?.selectedAnswerUuid;
  }
}

export class SmartSequenceAnswer {
  uuid: string;
  text: string;
  explanation?: string;
  score?: number;
  questionPairedUuid: string;

  constructor(answer?: SmartSequenceAnswer) {
    this.uuid = answer?.uuid;
    this.text = answer?.text;
    this.explanation = answer?.explanation;
    this.score = answer?.score;
    this.questionPairedUuid = answer?.questionPairedUuid;
  }
}

export class SmartSequenceParams {
  title: string;
  description?: string;
  objectives?: string;
  rules?: string;
  local: string;
  author: string;
  recommendedDuration?: number;
  canReplay?: boolean;
  totalNumberReplay?: number;
  endMessage?: string;
  failEndMessage: string;
  successEndMessage: string;
  hasValidationScore: boolean;
  validationScore: number;
  randomizeAnswers: boolean;
  thresholds: {
    first: number;
    second: number;
    // third: number;
  };
  graphicConfig?: SmartSequenceStyleParams;

  constructor(params?: SmartSequenceParams) {
    this.title = params?.title ?? null;
    this.description = params?.description ?? null;
    this.objectives = params?.objectives ?? null;
    this.graphicConfig = new SmartSequenceStyleParams(params?.graphicConfig);
    this.rules = params?.rules ?? null;
    this.author = params?.author ?? null;
    this.local = params?.local ?? null;
    this.recommendedDuration = params?.recommendedDuration ?? null;
    this.canReplay = params?.canReplay ?? false;
    this.totalNumberReplay = params?.totalNumberReplay ?? null;
    this.endMessage = params?.endMessage ?? null;
    this.failEndMessage = params?.failEndMessage || null;
    this.successEndMessage = params?.successEndMessage || null;
    this.hasValidationScore = params?.hasValidationScore ?? false;
    this.validationScore = params?.validationScore ?? 0;
    this.randomizeAnswers = params?.randomizeAnswers ?? false;
    this.thresholds = params?.thresholds ?? {
      first: 30,
      second: 50,
      // third: 80,
    };
  }
}

export class SmartSequenceStyleParams {
  primaryColor?: string;
  darkColor?: string;
  lightColor?: string;
  faviconUrl?: CustomImage;

  fontTitle?: Font;
  status: ProductGraphicPreferenceStatus;
  displayAttachedIllustration1: boolean;
  displayAttachedIllustration2: boolean;
  logoImage?: CustomImage | null;
  attachedIllustration1?: CustomImage | null;
  attachedIllustration2?: CustomImage | null;
  miniature?: CustomImage | null;
  show3dCinematic: boolean;

  constructor(params?: SmartSequenceStyleParams) {
    this.primaryColor = params?.primaryColor || '#43ae97';
    this.lightColor = params?.lightColor || '#FFFFFF';
    this.darkColor = params?.darkColor || '#000000';
    this.status = params?.status ? params.status : ProductGraphicPreferenceStatus.DEFAULT;

    this.faviconUrl = new CustomImage(
      params?.faviconUrl?.src
        ? params.faviconUrl
        : {
            src: 'assets/shared/images/branding/favicon-z.svg',
            type: BackgroundType.IMAGE,
          },
    );

    this.fontTitle = new Font(params?.fontTitle || { family: "'Ubuntu', sans serif" });

    this.logoImage = params?.logoImage ? new CustomImage(params.logoImage) : null;
    this.attachedIllustration1 = params?.attachedIllustration1 ? new CustomImage(params.attachedIllustration1) : null;
    this.attachedIllustration2 = params?.attachedIllustration2 ? new CustomImage(params.attachedIllustration2) : null;
    this.displayAttachedIllustration1 = params?.displayAttachedIllustration1 ?? true;
    this.displayAttachedIllustration2 = params?.displayAttachedIllustration2 ?? true;
    this.miniature = params?.miniature ? new CustomImage(params.miniature) : null;
    this.show3dCinematic = params?.show3dCinematic ?? true;

    // the null value is important in case of no background to display
    // the default lottie when there is no slideBackground and the light color is white
  }
}
